// ============================================================
// MANIC MEETER — English content: interface
// ============================================================

import { PowerupId } from "../../types";
import type { UiStrings } from "../contract";

export const UI: UiStrings = {
  meta: {
    pageTitle: "Manic Meeter — Meeting Hell",
    subtitle: "Survive meeting hell at MegaCorp™",
  },
  cookie: {
    text: "🍪 This site uses cookies and localStorage for login sessions and saved games. More in our",
    privacyLinkLabel: "privacy policy",
    accept: "Accept",
    decline: "Essential only",
  },
  controls: {
    soundTitle: "Toggle sound",
    soundOn: "🔊 SOUND",
    soundOff: "🔇 SOUND",
    themeTitle: "Toggle terminal mode",
    themeLabel: "⬛ RETRO",
    themeLabelModern: "◻️ MODERN",
    langTitle: "Auf Deutsch umschalten",
    langSwitchLabel: "🇩🇪 DE",
  },
  start: {
    btnLogin: "🔐 Log in",
    btnRegister: "📝 Register",
    btnHighscores: "🏆 High scores",
    btnAchievements: "🏅 Achievements",
    nameLabel: "Your name",
    namePlaceholder: "Max Mustermann",
    roleLabel: "Your role (= difficulty)",
    btnStart: "🏢 Enter the office",
    imprint: "Imprint",
    privacy: "Privacy",
    anonymousName: "Anon",
  },
  authStatus: {
    loggedIn: (username) => `✅ Logged in as <strong>${username}</strong>`,
    logout: "Log out",
    guest: "👤 Guest session · <em>Register to keep your progress!</em>",
    anonymous: "👤 Not logged in · You can play as a guest",
  },
  instructions: {
    heading: "📋 How to play",
    sections: [
      {
        heading: "🎯 Goal",
        paragraphs: [
          "Survive as many meetings as you can without falling asleep or dropping below the minimum wage of <strong>€1,000</strong>.",
        ],
      },
      {
        heading: "😴 Fatigue",
        paragraphs: [
          "Every time a colleague speaks, your fatigue rises. Let it fill up and you fall asleep — and lose salary.",
        ],
      },
      {
        heading: "🎮 Your turn",
        paragraphs: [
          "<strong>😎 Procrastinate</strong> — Cuts fatigue. But careful: get caught and it costs you salary!",
          "<strong>🎤 Contribute</strong> — If you are called on, you have to answer (questions, minigames). If nobody called on you, you deliver an unprompted monologue instead.",
        ],
      },
      {
        heading: "⚡ Being called on",
        paragraphs: [
          'Colleagues can single you out at any moment — watch for the <span class="address-hint">highlighted</span> giveaway! How often depends on your role.',
        ],
      },
      {
        heading: "🃏 Prep time & powerups",
        paragraphs: [
          "Before each meeting you draw a card — with luck you find something useful. In the shop you can spend <strong>Impostor Points</strong> on further upgrades.",
        ],
      },
      {
        heading: "🎭 Impostor Points",
        paragraphs: [
          "Every day you survive earns points, with a bonus for staying awake. The points survive Game Over.",
        ],
      },
      {
        heading: "⌨️ Controls",
        paragraphs: [
          "<strong>Mouse / touch</strong> — Click buttons for actions and answers.",
          "<strong>Escape</strong> — Quit the run and return to the start screen.",
        ],
      },
    ],
    okBtn: "✅ Got it — let's go!",
  },
  status: {
    day: "Day",
    salary: "Salary",
    fatigue: "Fatigue",
    meetings: "Meetings",
    points: "Impostor Points",
  },
  infoPanel: {
    toggleClosed: "📋 Info ▼",
    toggleOpen: "📋 Info ▲",
    perksHeading: "🏷️ Perks",
    powerupsHeading: "🎒 Powerups",
    statsHeading: "📊 Stats",
    noPerks: "No active perks",
    noPowerups: "No active powerups",
    wholeMeeting: "meeting",
    turnsLeft: (turns) => `${turns} turn(s)`,
    turnsShort: (turns) => `${turns}T`,
    remaining: "left",
  },
  meeting: {
    you: "🧑 YOU",
    directionForward: "→ Forwards",
    directionBackward: "← Backwards",
    participants: (count) => `👥 ${count} attendees`,
    rounds: (count) => `${count} round(s)`,
    tasks: (count) => `${count} tasks`,
    roundEnded: (roundsLeft) => `── Round over. ${roundsLeft} round(s) to go ──`,
    directionReversed: "🔀 The order reverses!",
    next: (meetingName) => `📬 Next meeting: <strong>${meetingName}</strong>`,
    survived: (meetingName) => `✅ Survived "${meetingName}"!`,
    enter: "🚪 Enter the meeting",
    enterOnline: "📡 Join the call",
    continueToNext: "On to the next meeting",
    schedule: "Today's calendar",
    scheduleToday: (count) => `${count} meeting(s) today.`,
  },
  actions: {
    procrastinate: "😎 Procrastinate",
    action: "🎤 Contribute",
    powerup: "🎒 Powerup",
    skip: "⏭ Skip",
    continue: "Continue",
    walkOn: "🚶 Walk on",
    done: "✅ Done",
    close: "Close",
    startDay: "Start the day",
    endOfDay: "Clocking off! Next day →",
    playAgain: "🔄 Play again",
    yourAnswer: (answer) => `💬 Your answer: "${answer}"`,
    noAnswer: "⏰ No answer...",
    powerupUsedTurnOver: "⏰ You used a powerup. Turn over.",
    staredIntoSpace: "⏰ You stare into the middle distance… nothing happens.",
  },
  minigames: {
    announce: {
      question: "QUESTION!",
      brainstorming: "BRAINSTORMING!",
      teamBuilding: "TEAM BUILDING!",
      buzzwordBingo: "BUZZWORD BINGO!",
      emailSorting: "EMAIL TRIAGE!",
      calendarTetris: "CALENDAR TETRIS!",
      codeReview: "CODE REVIEW!",
      aiHallucination: "AI HALLUCINATION!",
    },
    announceLine: (headline, context) => `⚡ <strong>${headline}</strong> ${context}`,
    assistantAnnounce: (tool) => `Assistant: <strong>${tool}</strong> — flag whatever it made up!`,
    memoriseOrderShort: "Memorise the order and click them from memory!",
    typeLetterByLetter: (word) => `Type it letter by letter: <strong>${word}</strong>`,
    typeHint: "Type it letter by letter!",
    repeatSequence: "Repeat the sequence! (W=↑ S=↓ A=← D=→)",
    sequenceHint: "W=↑  S=↓  A=←  D=→",
    memoriseOrder: "Memorise the order, then click the buzzwords from memory!",
    memorising: "Memorise the order...",
    clickInOrder: (total) => `Click them in the order you memorised: 1/${total}`,
    sortEmails: "Sort every email: Spam, Important or Delegate!",
    emailSpam: "🚫 Spam",
    emailImportant: "⚡ Important",
    emailDelegate: "👋 Delegate",
    findFreeSlot: "Find the slot where EVERY attendee is free!",
    slotQuestion: "When is everyone actually free? Click the time slot!",
    codeLanguage: (language) => `Language: <strong>${language}</strong> — find the fix!`,
    findTheFix: "Find the fix!",
    assistantLabel: (tool) => `🤖 ${tool}`,
    promptLabel: (task) => `Prompt: "${task}"`,
    markHallucinations: (count) => `Flag ${count} invented statement${count === 1 ? "" : "s"}.`,
    evaluating: "Scoring...",
    correct: "✓ Correct!",
    wrong: (expected) => `✗ Wrong! Expected: ${expected}`,
    wrongOrder: "✗ Wrong order! Start again.",
    correctWas: (correct) => `✗ Wrong! It was: ${correct}`,
    progress: (done, total) => `✓ ${done}/${total}`,
    slotCorrect: (slot) => `✓ Correct! Everyone is free at ${slot}!`,
    slotWrong: (slot) => `✗ Wrong! ${slot} was the free slot!`,
    hallucinationsCaught: (hits, wanted) => `✓ Caught ${hits}/${wanted} hallucinations!`,
    hallucinationsMissed: (hits, wanted) => `✗ Only ${hits}/${wanted}. The rest ships as written.`,
  },
  outcomes: {
    brainstormWin: "✅ Perfect! Buzzword neutralised. (-10 fatigue)",
    brainstormFail: "❌ Too slow! The meeting gets extended.",
    simonWin: "✅ Synchronised! Team spirit +1. (-8 fatigue)",
    simonFail: "❌ Off the beat! Awkward. (+5 fatigue)",
    bingoWin: "✅ Bingo! You know your buzzwords. (-12 fatigue)",
    bingoFail: "❌ Wrong order! Excruciating silence. (+5 fatigue)",
    emailPerfect: (total) => `✅ All ${total} correct! Inbox zero! (-15 fatigue)`,
    emailPartial: (correct, total) => `⚠️ ${correct}/${total} correct. Could be worse. (-5 fatigue)`,
    emailFail: "❌ None correct! Your inbox detonates. (+8 fatigue)",
    kalenderWin: "✅ Perfectly scheduled! You are a calendar professional. (-10 fatigue)",
    kalenderFail: "❌ Double booking! The boss is not amused. (+5 fatigue)",
    codeReviewWin: "✅ Bug fixed! Clean code. (-12 fatigue)",
    codeReviewFail: "❌ Wrong fix! The bug is now a feature. (+5 fatigue)",
    kiPerfect: (hits, total) => `✅ ${hits}/${total} — every hallucination caught! The slide is now even true. (-12 fatigue)`,
    kiPartial: (hits, total) => `⚠️ ${hits}/${total} caught. The rest is in the minutes now. (-5 fatigue)`,
    kiFail: "❌ Caught nothing! Half the company believes it now. (+6 fatigue)",
  },
  boss: {
    intro: (tasks) => `🏛️ <strong>BOARD MEETING!</strong> The board has ${tasks} tasks for you. No procrastinating here!`,
    stakes: (penalty, bonus) => `💰 Wrong answer = -€${penalty} | All correct = +€${bonus} bonus!`,
    question: (prompt) => `❓ <strong>QUESTION!</strong> ${prompt}`,
    taskHeader: (current, total) => `── Task ${current} of ${total} ──`,
    announce: "🏛️ <strong>Board meeting!</strong> No way out.",
    noEscape: "No way out.",
    enter: "🏛️ Enter the board meeting",
    survived: "✅ Board meeting survived!",
    questionWin: "✅ Correct! The board nods approvingly.",
    questionFail: (penalty) => `❌ Wrong! Disappointed looks around the table. -€${penalty}`,
    brainstormWin: "✅ Perfect! The board is impressed.",
    brainstormFail: (penalty) => `❌ Too slow! The CFO shakes their head. -€${penalty}`,
    simonWin: "✅ Synchronised! The board applauds.",
    simonFail: (penalty) => `❌ Off the beat! Embarrassing, in front of the board. -€${penalty}`,
    bingoWin: "✅ Bingo! Linguistic competence confirmed.",
    bingoFail: (penalty) => `❌ Wrong order! The board makes a note of that. -€${penalty}`,
    emailWin: (total) => `✅ All ${total} correct! Inbox management at board level.`,
    emailFail: (correct, total, penalty) => `❌ ${correct}/${total} correct. The board is unconvinced. -€${penalty}`,
    kalenderWin: "✅ Perfectly scheduled! Time-management professional.",
    kalenderFail: (penalty) => `❌ Double booking! The board sighs. -€${penalty}`,
    codeReviewWin: "✅ Bug fixed! Clean code approved.",
    codeReviewFail: (penalty) => `❌ Wrong fix! The CTO shakes their head. -€${penalty}`,
    kiWin: "✅ Every hallucination caught! The board will never find out.",
    kiFail: (hits, total, penalty) => `❌ ${hits}/${total} caught. The board now quotes a study that does not exist. -€${penalty}`,
    flawless: (bonus) => `🎉 <strong>Flawless!</strong> Not one mistake! The board is delighted. +€${bonus} raise!`,
    withMistakes: (mistakes, bonus) => `✅ Board meeting survived with ${mistakes} mistake(s). +€${bonus}`,
    noBonus: (mistakes) => `😰 Board meeting survived, but ${mistakes} mistakes. No bonus.`,
  },
  prep: {
    heading: "🃏 <strong>Prep time!</strong> Draw one of three cards — you might find something useful!",
    card: (index) => `🃏 Card ${index}`,
    emptyCard: "📋 Blank card. No luck this time.",
    found: (name, description) => `🎉 <strong>${name}!</strong> ${description}`,
    pizzaSkip: "🍕 Pizza is here! You skip this meeting entirely.",
    vpnSkip: "🔒 <strong>VPN access!</strong> You slipped out of the meeting unnoticed.",
    vpnSkipped: (meetingName) => `📬 "${meetingName}" was skipped.`,
  },
  powerups: {
    activation: {
      [PowerupId.Kaffee]: "☕ {name}: −25 fatigue!",
      [PowerupId.HandyAkku]: "📱 {name}: your next procrastination is completely safe!",
      [PowerupId.NoiseCancelling]: "🎧 {name}: colleague fatigue −50% this meeting!",
      [PowerupId.FakeNotizen]: "📋 {name}: the next question answers itself, correctly!",
      [PowerupId.LaptopTarnung]: "💻 {name}: for 3 rounds, procrastinating looks like work!",
      [PowerupId.PizzaBestellen]: "🍕 {name}: you skip this meeting entirely!",
      [PowerupId.MuteButton]: "🔇 {name}: immune to being called on this meeting!",
      [PowerupId.RemoteJoker]: "📡 {name}: the meeting moves online! Less chance of being called on!",
      [PowerupId.BullshitBingo]: "📊 {name}: buzzwords are being counted — bonus at the end of the meeting!",
      [PowerupId.PromptBibliothek]: "📚 {name}: the prompts are ready — all minigame timers +60%!",
      [PowerupId.KiZusammenfassung]: "📝 {name}: the AI already summarised it — 2 rounds fewer!",
      [PowerupId.KiAgent]: "🤖 {name}: worked through the night — +150€, but proofreading it costs you (+10 fatigue)!",
    },
    useHeading: "🎒 Use a powerup?",
    inventoryCount: (count) => `(${count}×)`,
  },
  shop: {
    heading: "🛒 Shop",
    points: "Impostor Points",
    statUpgrades: "📈 Stat upgrades",
    powerups: "🎒 Powerups",
    permanentUnlocks: "🏆 Permanent unlocks",
    permanentHint: "(Unlocked once your lifetime total is high enough — they cost nothing!)",
    completed: "✅ Unlocked",
    level: (level, max) => `Level ${level}/${max}`,
    owned: (count) => `📦 ${count}×`,
    usableLabel: "Usable in",
    usablePrep: "prep",
    usableMeeting: "meeting",
    usableBoth: "prep + meeting",
    effect: "Effect",
    maxed: "MAX",
    unlockFrom: (cost) => `From ${cost} 🎭 total`,
    close: "✅ Done",
  },
  vendor: {
    heading: "🛍️ Opportunity",
    yourSalary: "💰 Your salary",
    walkOn: "🚶 Walk on",
  },
  day: {
    welcome: (name, role) => `Welcome, <strong>${name}</strong>! You are ${role} at <strong>MegaCorp™</strong>.`,
    startingSalary: (salary, minWage) => `Starting salary: €${salary}. Below €${minWage} = game over.`,
    goal: "Survive as many meetings as you can!",
    dayHeader: (day, meetings) => `📅 Day ${day} — ${meetings} meeting(s) today.`,
    activePerks: (perks) => `🏷️ Active perks: ${perks}`,
    attestActive: (days) => `📋 <strong>SICK NOTE active!</strong> ${days} day(s) left — enormous bonuses!`,
    attestFound:
      "📋🏆 <strong>SICK NOTE FOUND!</strong> A doctor's certificate — the most powerful document in German "
      + "working life! Five days of absurd bonuses!",
    attestEffects: "⚡ -70% fatigue, 3× recovery, -80% chance of being called on, -90% penalties, +€100/day!",
    perkBonus: "Bonus of the day",
    perkMalus: "Curse of the day",
    perkLine: (emoji, kind, name, description) => `${emoji} <strong>${kind}:</strong> ${name} — ${description}`,
    perkLateGaming: "🎮 Up late gaming last night... (+20 fatigue)",
    perkTraffic: "🚗 Stuck in traffic... (+15 fatigue)",
    meetingCancelled: (meetingName) => `⏭️ "${meetingName}" is cancelled!`,
    homeoffice: "🏠 <strong>HOME OFFICE!</strong> You are working from home today — every meeting is online!",
    weekendBonusSalary: (amount, event) => `💰 +€${amount} bonus from "${event}"!`,
    weekendBonusFatigue: (amount, event) => `☕ -${amount} fatigue thanks to "${event}"!`,
    weekendExtraMeetings: (count, event) => `📅 +${count} extra meeting(s) from "${event}"!`,
    daySurvived: (day) => `☀️ Day ${day} survived!`,
    pointsEarned: (points, noSleepBonus, total) =>
      `🎭 +${points} Impostor Points${noSleepBonus ? " (bonus: stayed awake!)" : ""}! Total: ${total}`,
    newAchievements: (list) => `🏆 New achievements: ${list}`,
    summaryHeading: "Day in review",
    chartMeetingsToday: "📊 Meetings today",
    chartFatigueCausers: "😴 Fatigue by colleague (all time)",
    chartDeadliest: "💀 Deadliest meetings (all time)",
  },
  panels: {
    statMeetings: "Meetings",
    statCaught: "Caught",
    statFellAsleep: "Fell asleep",
    statMinigames: "Minigames",
    statProcrastinated: "Procrastinated",
    attestPill: (days) => `📋 Sick note (${days}d)`,
    attestTooltip: (days) => `A doctor's certificate — enormous bonuses for ${days} day(s)`,
    online: "📡 ONLINE",
    offline: "🏢 IN PERSON",
    round: (current, total) => `🔄 Round ${current}/${total}`,
    newDayHeading: (day) => `☀️ Day ${day} — another day at the office`,
    todaysPlan: "On today's agenda:",
    prepHeading: "☕ Prep time",
    prepNextMeeting: (meetingName) => `Next meeting: <strong>${meetingName}</strong>`,
    prepHint: "Draw a card — the meeting might just go remote!",
    summaryMeetingsSurvived: "🏆 Meetings survived",
    summaryMeetingsSkipped: "⏭️ Meetings skipped",
    summaryTimesCaught: "💀 Times caught",
    summaryTimesFellAsleep: "😴 Times asleep",
    summaryMonologues: "🎤 Monologues",
    summaryMinigamesWon: "🎮 Minigames won",
    summaryMinigamesLost: "🎮 Minigames lost",
    summarySalary: "💰 Salary",
    summaryRecord: "Record",
    summaryFatigue: "😴 Fatigue",
    finalDaysSurvived: "📊 Days survived",
    finalMeetingsSurvived: "🏆 Meetings survived",
    finalPeakSalary: "💰 Peak salary",
    weekendHint: "⚠️ Bonus meetings! But there are rewards too...",
    endingsCollection: "📖 Endings collected",
    achievementsModalHeading: (unlocked, total) => `🏆 Achievements (${unlocked}/${total})`,
  },
  encounter: {
    fatigueDelta: (sign, amount) => `😴 ${sign}${amount} fatigue`,
    salaryDelta: (sign, amount) => `💰 ${sign}€${amount} salary`,
    newPerk: (emoji, name, description) => `${emoji} New perk: <strong>${name}</strong> — ${description}`,
    perkRemoved: (name) => `✨ Perk removed: <strong>${name}</strong>`,
    powerupFound: (name, description) => `🎁 <strong>Powerup found:</strong> ${name} — ${description}`,
  },
  bingo: {
    complete: "🎯 <strong>BINGO!</strong> Every goal met! -20 fatigue and +€100 bonus!",
    heading: "🎯 Meeting bingo",
    bingoLine: "🎉 BINGO!",
    progress: (done, total) => `(${done}/${total})`,
    buzzwordBonus: (count, bonus) => `📊 Bullshit bingo! ${count} buzzwords heard — <strong>-${bonus} fatigue!</strong>`,
  },
  gameOver: {
    heading: "💀 GAME OVER 💀",
    bonusPoints: (points) => `🎭 +${points} bonus Impostor Points for this ending!`,
    keptPoints: (points, total) => `🎭 Impostor Points kept: <strong>${points}</strong> (lifetime earned: ${total})`,
    unknownEnding: "???",
    playAgain: "🔄 Play again",
  },
  escape: {
    heading: "⏸️ Quit the run?",
    body: "Your progress (Impostor Points and upgrades) is saved. The current run ends here.",
    confirm: "🚪 Quit",
    cancel: "↩️ Keep playing",
  },
  achievements: {
    heading: "🏅 Achievements",
    unlocked: (emoji, name, description) => `🏆 <strong>ACHIEVEMENT UNLOCKED:</strong> ${emoji} ${name} — ${description}`,
    unlockedOf: (unlocked, total) => `${unlocked}/${total} achievements unlocked`,
    locked: "🔒",
  },
  auth: {
    loginHeading: "🔐 Log in",
    registerHeading: "📝 Register",
    usernameOrEmail: "Username or email",
    username: "Username",
    email: "Email",
    password: "Password",
    passwordMin: "Password (min. 8 characters)",
    passwordConfirm: "Confirm password",
    submitLogin: "Log in",
    submitRegister: "Register",
    cancel: "Cancel",
    fillAllFields: "Please fill in every field.",
    passwordMismatch: "The passwords do not match.",
    passwordTooShort: "The password must be at least 8 characters.",
    loginFailed: "Login failed.",
    registerFailed: "Registration failed.",
  },
  highscores: {
    heading: "🏆 High scores",
    tabScore: "🏅 Score",
    tabDays: "📅 Days",
    tabSalary: "💰 Salary",
    tabMeetings: "🏆 Meetings",
    colName: "Name",
    colRole: "Role",
    colScore: "Score",
    colDaysMeetings: "Days/meetings",
    colSalary: "Salary",
    empty: "No high scores yet.",
    close: "Close",
  },
  api: {
    serverError: "Server error",
    connectionFailed: "Could not reach the server.",
  },
  gameOverReasons: {
    belowMinWage: (salary, minWage) =>
      `Your salary (€${salary}) has fallen below the minimum wage (€${minWage}). You have been let go.`,
  },
  turns: {
    caught: (action, penalty, escalation) => `💀 ${action} Salary docked: -€${penalty}${escalation}`,
    escalation: (times) => ` (escalation ×${times}!)`,
    procrastinateCritical: (action, reduction) => `⚡ ${action} CRITICAL! (-${reduction} fatigue)`,
    monologue: (rounds) => `🎤 You deliver an unprompted monologue. The meeting is extended by ${rounds} round(s)!`,
    answerCritical: (reduction) => `✅⚡ CRITICAL! Good answer! (-${reduction} fatigue)`,
    answerGood: (reduction) => `✅ Good answer! The meeting carries on as normal. (-${reduction} fatigue)`,
    answerWrong: (rounds) => `❌ Wrong answer! You started a discussion. +${rounds} round(s)!`,
    fellAsleep: (penalty) => `😴💤 You FELL ASLEEP! A dressing-down from the boss. Salary docked: -€${penalty}!`,
    fakeNotes: "📋 Fake notes! You act as though you wrote all of it down.",
  },
};
